import { Close as CloseIcon, InfoOutlined } from '@mui/icons-material'
import { Alert, Backdrop, CircularProgress, IconButton, Popover, Tab, Tabs } from '@mui/material'
import { SyntheticEvent, useContext, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { dismissMicroLearning } from '../../api/api'
import { DismissReasonId, ExperimentDisposition, ExperimentLinkTarget, UserRole } from '../../api/models'
import CustomDialog from '../../components/dialogs/CustomDialog/CustomDialog'
import { AppStateContext } from '../../state/AppProvider'
import styles from './MicroLearningDetail.module.css'
import ApproveModal from './components/ApproveModal'
import CompletedExperimentBanner from './components/CompletedExperimentBanner'
import DetailHeader from './components/DetailHeader'
import DetailTabsContent, { DETAIL_TAB_CONFIG, DetailTabValue } from './components/DetailTabsContent'
import DismissModal from './components/DismissModal'
import ExperimentDispositionModal from './components/ExperimentDispositionModal'
import ReprocessWarning from './components/ReprocessWarning'
import { useExport } from './hooks/useExport'
import { useExperimentManagement } from './hooks/useExperimentManagement'
import { useMicroLearningDetail } from './hooks/useMicroLearningDetail'
import { useMicroLearningReprocess } from './hooks/useMicroLearningReprocess'

const MicroLearningDetail = () => {
  const navigate = useNavigate()
  const { mlId = '' } = useParams()
  const appStateContext = useContext(AppStateContext)
  const [activeTab, setActiveTab] = useState<DetailTabValue>('summary')
  const [approveModalOpen, setApproveModalOpen] = useState(false)
  const [dismissStep, setDismissStep] = useState<'closed' | 'warning' | 'confirm' | 'disposition'>('closed')
  const [dismissReason, setDismissReason] = useState<DismissReasonId | ''>('')
  const [dismissContext, setDismissContext] = useState('')
  const [isDismissing, setIsDismissing] = useState(false)
  const [experimentDispositions, setExperimentDispositions] = useState<
    Record<string, ExperimentDisposition | undefined>
  >({})
  const [pendingRemoval, setPendingRemoval] = useState<{ code: string; target: ExperimentLinkTarget } | null>(null)
  const [historyRefreshSignal, setHistoryRefreshSignal] = useState(0)
  const {
    isLoading,
    hasError,
    isNotFound,
    data,
    showScalePotential,
    notProcessed,
    reviewDecision,
    refetch,
    patchData,
  } = useMicroLearningDetail(mlId)
  const { isExporting, triggerExport } = useExport(mlId)

  useEffect(() => {
    if (!showScalePotential && activeTab === 'scale-potential') {
      setActiveTab('summary')
    }
  }, [activeTab, showScalePotential])

  const visibleTabs = useMemo(
    () => DETAIL_TAB_CONFIG.filter(tab => showScalePotential || tab.value !== 'scale-potential'),
    [showScalePotential],
  )

  const refreshHistory = useCallback(() => {
    setHistoryRefreshSignal(s => s + 1)
  }, [])

  const handleExperimentsChanged = useCallback(() => {
    refetch()
    refreshHistory()
  }, [refetch, refreshHistory])

  const { isSubmitting: isManagingExperiments, removeExperiment } = useExperimentManagement(
    mlId,
    handleExperimentsChanged,
  )
  const role = appStateContext?.state.user?.role
  const canManageExperiments = role === UserRole.ADMIN || role === UserRole.DECODER

  const { isReprocessing, reprocess } = useMicroLearningReprocess(mlId, handleExperimentsChanged)
  const showReprocess = data?.status === 'not_processed'

  const handleEditSuccess = useCallback(
    (partial: Partial<typeof data>) => {
      patchData(partial as any)
      refreshHistory()
    },
    [patchData, refreshHistory],
  )

  const handleApproveSuccess = useCallback(() => {
    refetch()
    refreshHistory()
  }, [refetch, refreshHistory])

  const handleRegenerateSuccess = useCallback(() => {
    refetch()
    refreshHistory()
  }, [refetch, refreshHistory])

  const handleTabChange = (_: SyntheticEvent, newValue: DetailTabValue) => {
    setActiveTab(newValue)
  }

  const [experimentsInfoAnchor, setExperimentsInfoAnchor] = useState<HTMLElement | null>(null)
  const [scalePotentialInfoAnchor, setScalePotentialInfoAnchor] = useState<HTMLElement | null>(null)

  const tabLabel = (tabValue: DetailTabValue, label: string) => {
    if (tabValue === 'experiments' || tabValue === 'scale-potential') {
      return (
        <span className={styles.tabLabelWithInfo}>
          {label}
          <span
            className={styles.tabInfoIconWrapper}
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              if (tabValue === 'experiments') {
                setExperimentsInfoAnchor(e.currentTarget)
              } else {
                setScalePotentialInfoAnchor(e.currentTarget)
              }
            }}
            onMouseDown={e => e.stopPropagation()}>
            <InfoOutlined className={styles.tabInfoIcon} />
          </span>
        </span>
      )
    }

    return label
  }

  const resetDismissFlow = () => {
    setDismissStep('closed')
    setDismissReason('')
    setDismissContext('')
    setIsDismissing(false)
    setExperimentDispositions({})
  }

  const openDismissFlow = () => {
    const emptyDispositions = Object.fromEntries(
      (data?.experiments ?? []).map(experiment => [experiment.code, undefined]),
    )
    setExperimentDispositions(emptyDispositions)
    setDismissStep(data?.status === 'approved' ? 'warning' : 'confirm')
  }

  const handleDismissSave = async () => {
    if (!dismissReason || !data || !mlId) return
    const payloadDispositions = data.experiments
      .map(experiment => {
        const disposition = experimentDispositions[experiment.code]
        if (!disposition) return null
        return { code: experiment.code, disposition }
      })
      .filter((item): item is { code: string; disposition: ExperimentDisposition } => item !== null)

    if (payloadDispositions.length !== data.experiments.length) {
      return
    }

    setIsDismissing(true)
    const result = await dismissMicroLearning(mlId, {
      selected_reason: dismissReason,
      reason: dismissContext.trim() || undefined,
      experiment_dispositions: payloadDispositions,
    })
    setIsDismissing(false)

    if (result.success) {
      resetDismissFlow()
      refetch()
      refreshHistory()
      appStateContext?.dispatch({
        type: 'SET_SNACKBAR_STATE',
        payload: { message: 'Micro-learning dismissed successfully', severity: 'success' },
      })
      return
    }

    appStateContext?.dispatch({
      type: 'SET_SNACKBAR_STATE',
      payload: {
        message: 'Failed saving Micro-learning changes. Try again later',
        severity: 'warning',
      },
    })
  }

  const handleConfirmRemoval = useCallback(async () => {
    if (!pendingRemoval) return
    // Keep the dialog open on failure so the user can retry; the hook already
    // surfaces the backend error via Snackbar.
    const success = await removeExperiment(pendingRemoval.code, pendingRemoval.target)
    if (success) {
      setPendingRemoval(null)
    }
  }, [pendingRemoval, removeExperiment])

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>Micro Learnings</h1>
        <p className={styles.headerDescription}>Review the AI-generated micro-learning and decide how to proceed.</p>
      </div>

      <div className={styles.breadcrumb}>
        <button type='button' className={styles.breadcrumbLink} onClick={() => navigate('/app/micro-learnings')}>
          Micro Learnings
        </button>
        <span className={styles.breadcrumbSeparator} aria-hidden='true'>
          {'>'}
        </span>
        <span className={styles.breadcrumbCurrent}>Micro-Learning Detail</span>
      </div>
      {hasError ? (
        <Alert severity='error'>
          {isNotFound
            ? 'This micro-learning could not be found.'
            : 'Unable to load micro-learning detail. Please try again.'}
        </Alert>
      ) : (
        <>
          <DetailHeader
            data={data}
            reviewDecision={reviewDecision}
            onApproveClick={() => setApproveModalOpen(true)}
            onEditSuccess={handleEditSuccess}
            onRegenerateSuccess={handleRegenerateSuccess}
            isExporting={isExporting}
            triggerExport={triggerExport}
            onDismissClick={openDismissFlow}
          />
          {data?.has_newly_completed_experiments === true && <CompletedExperimentBanner />}
          {showReprocess && (
            <ReprocessWarning
              failureDetails={data?.processing?.failure_details}
              canReprocess={canManageExperiments}
              isReprocessing={isReprocessing}
              onReprocess={reprocess}
            />
          )}
          <div className={styles.tabsShell}>
            <Tabs value={activeTab} onChange={handleTabChange} className={styles.tabs}>
              {visibleTabs.map(tab => (
                <Tab key={tab.value} value={tab.value} label={tabLabel(tab.value, tab.label)} />
              ))}
            </Tabs>

            <DetailTabsContent
              activeTab={activeTab}
              data={data}
              notProcessed={notProcessed}
              historyRefreshSignal={historyRefreshSignal}
              onExperimentsChanged={handleExperimentsChanged}
              canManage={canManageExperiments}
              isRemoving={isManagingExperiments}
              onRequestRemove={(code, target) => setPendingRemoval({ code, target })}
            />
          </div>
        </>
      )}

      <ApproveModal
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        mlId={mlId}
        data={data}
        onSuccess={handleApproveSuccess}
      />
      <CustomDialog
        title='Attention'
        open={dismissStep === 'warning'}
        setClose={resetDismissFlow}
        primaryAction={() => setDismissStep('confirm')}
        secondaryAction={resetDismissFlow}
        primaryActionButtonText='Continue'
        secondaryActionButtonText='Cancel'>
        <p className={styles.attentionDialogBody}>
          This micro-learning is currently <strong>approved</strong>. Dismiss will revert it.
        </p>
      </CustomDialog>
      <DismissModal
        open={dismissStep === 'confirm'}
        data={data}
        selectedReason={dismissReason}
        additionalContext={dismissContext}
        onSelectedReasonChange={setDismissReason}
        onAdditionalContextChange={setDismissContext}
        onClose={resetDismissFlow}
        onContinue={() => setDismissStep('disposition')}
      />
      <ExperimentDispositionModal
        open={dismissStep === 'disposition'}
        data={data}
        dispositions={experimentDispositions}
        isSaving={isDismissing}
        onSetDisposition={(code, disposition) => {
          setExperimentDispositions(prev => ({ ...prev, [code]: disposition }))
        }}
        onSetAllDisposition={disposition => {
          setExperimentDispositions(
            Object.fromEntries((data?.experiments ?? []).map(experiment => [experiment.code, disposition])),
          )
        }}
        onClose={resetDismissFlow}
        onSave={handleDismissSave}
      />
      <CustomDialog
        title='Remove this experiment?'
        open={pendingRemoval !== null}
        setClose={() => {
          if (!isManagingExperiments) setPendingRemoval(null)
        }}
        primaryAction={handleConfirmRemoval}
        secondaryAction={() => setPendingRemoval(null)}
        primaryActionButtonText='Remove'
        secondaryActionButtonText='Cancel'
        primaryActionDisabled={isManagingExperiments}
        secondaryActionDisabled={isManagingExperiments}>
        <p className={styles.attentionDialogBody}>
          This will remove experiment <strong>{pendingRemoval?.code}</strong> from this micro-learning. The experiment
          itself will not be deleted.
        </p>
      </CustomDialog>

      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>

      <Popover
        open={Boolean(experimentsInfoAnchor)}
        anchorEl={experimentsInfoAnchor}
        onClose={() => setExperimentsInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              borderRadius: '8px',
              boxShadow: '0px 1px 3px 1px rgba(0,0,0,0.15), 0px 1px 2px 0px rgba(0,0,0,0.3)',
            },
          },
        }}>
        <div className={styles.infoPopoverContainer}>
          <div className={styles.infoPopoverHeader}>
            <h3 className={styles.infoPopoverTitle}>How Experiments Are Grouped</h3>
            <IconButton
              size='small'
              onClick={() => setExperimentsInfoAnchor(null)}
              className={styles.infoPopoverCloseButton}>
              <CloseIcon className={styles.infoPopoverCloseIcon} />
            </IconButton>
          </div>
          <p className={styles.infoPopoverDescription}>
            The pipeline groups experiments into micro-learnings based on shared characteristics: the same Media Type
            Objective (MTO), a similar Key Question, and comparable hypotheses. Each micro-learning represents a cluster
            of experiments that tested similar ideas, making the combined insight stronger than any single experiment.
          </p>
        </div>
      </Popover>

      <Popover
        open={Boolean(scalePotentialInfoAnchor)}
        anchorEl={scalePotentialInfoAnchor}
        onClose={() => setScalePotentialInfoAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              borderRadius: '8px',
              boxShadow: '0px 1px 3px 1px rgba(0,0,0,0.15), 0px 1px 2px 0px rgba(0,0,0,0.3)',
            },
          },
        }}>
        <div className={styles.infoPopoverContainer}>
          <div className={styles.infoPopoverHeader}>
            <h3 className={styles.infoPopoverTitle}>What Is Scale Potential</h3>
            <IconButton
              size='small'
              onClick={() => setScalePotentialInfoAnchor(null)}
              className={styles.infoPopoverCloseButton}>
              <CloseIcon className={styles.infoPopoverCloseIcon} />
            </IconButton>
          </div>
          <p className={styles.infoPopoverDescription}>
            When a micro-learning is generated, the system selects the most ready experiments based on their readiness
            scores. Additional experiments in the same group that were not used for generation are marked as &quot;Scale
            Potential&quot; &mdash; they show promising signals and may be relevant for further evaluation or future
            scaling.
          </p>
        </div>
      </Popover>
    </div>
  )
}

export default MicroLearningDetail
